import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { withMicroSites } from '../../hoc/withMicroSites';
import { useNavigate, useParams } from 'react-router-dom';
import MicroSiteAccordian from '../microsite-accordian';
import NewEditMicrosite from './edit/NewEditMicrosite';
import { getRequest } from '../../api/http';
import userServiceApi from '../../api/userServiceApi';
import { toast } from 'react-toastify';
import { useFetchOrganizationDetails } from '../../custom-hooks/useFetchOrganizationDetails';
import { useStore } from '../../store';
import { filterTabsBySubscriptionPlan } from '../app-features/planTabUtils';
import {
    cachePlanScopedTabs,
    MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
} from '../app-features/missingTabsSettingsUtils';
import { useSettings } from '../../custom-hooks/useSettings';

function MicroSites({ micrositeData }) {
    const navigate = useNavigate();
    const { generatingAI } = useStore();
    const { micrositeId } = useParams();
    const { fetchOrganizationDetails } = useFetchOrganizationDetails();
    const { getSettings } = useSettings();

    const [didProSettingsUpdate, setDidProSettingsUpdate] = useState(false)
    const [newProSettings, setNewProSettings] = useState([])
    const [loadingProSettings, setLoadingProSettings] = useState(false)

    // Fetch tabs-settings once per microsite (cached/deduped in useSettings).
    // Kept here so NewEditMicrosite remounts do not re-trigger the API.
    useEffect(() => {
        if (micrositeId) {
            getSettings(micrositeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [micrositeId])

    // Clear tab config only when switching microsites so the editor is not
    // remounted on unrelated didProSettingsUpdate / AI re-render toggles.
    useEffect(() => {
        setNewProSettings([])
    }, [micrositeId])

    useEffect(() => {
        if (!micrositeId) {
            return
        }
        if (
            Array.isArray(micrositeData) &&
            !micrositeData.some((site) => String(site.id) === String(micrositeId))
        ) {
            return
        }
        let cancelled = false
        const getProSettings = async () => {
            try {
                const [tabsResponse, plansResponse] = await Promise.all([
                    getRequest(userServiceApi.getMicroSiteTabConfig(micrositeId)),
                    getRequest(userServiceApi.handleFetchPlans(micrositeId)).catch(() => null),
                ]);

                if (!cancelled && tabsResponse?.data) {
                    const planScopedTabs = filterTabsBySubscriptionPlan(
                        tabsResponse.data,
                        plansResponse?.data?.planDetails,
                    );
                    cachePlanScopedTabs(micrositeId, planScopedTabs);
                    setNewProSettings(() => [...planScopedTabs]);
                }
            } catch (error) {
                if (!cancelled) {
                    toast.error("Failed to fetch site features")
                }
            }
            finally {
                if (!cancelled) {
                    setLoadingProSettings(false)
                }
            }
        }

        setLoadingProSettings(true)
        const FetchProSettingsDelay = setTimeout(() => {
            getProSettings();
        }, 300)

        return (() => {
            cancelled = true
            clearTimeout(FetchProSettingsDelay)
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [didProSettingsUpdate, micrositeId, generatingAI?.reRenderMicroSite])

    useEffect(() => {
        const onProSettingsUpdated = (event) => {
            const updatedMicrositeId = event?.detail?.micrositeId;
            if (
                updatedMicrositeId &&
                micrositeId &&
                String(updatedMicrositeId) === String(micrositeId)
            ) {
                setDidProSettingsUpdate((prev) => !prev);
            }
        };

        window.addEventListener(
            MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
            onProSettingsUpdated,
        );
        return () => {
            window.removeEventListener(
                MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
                onProSettingsUpdated,
            );
        };
    }, [micrositeId]);

    useEffect(() => {
        const checkOrgDetails = async () => {
            const filled = await fetchOrganizationDetails();
            if (filled === false) {
                navigate("/edit-organization", {
                    state: { tab: "organizationDetailsTab" },
                });
                localStorage.setItem("page", "organisationDetails");
            }
            localStorage.setItem("isOrganizationProfileFilled", filled);
        };
        checkOrgDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <div direction="vertical" style={{ width: '100%' }}>
            {
                micrositeData.map(microSite => {
                    const isActive = String(microSite.id) === String(micrositeId)
                    return <MicroSiteAccordian key={microSite.id} data={microSite} activeKey={micrositeId} micrositeData={micrositeData} setDidProSettingsUpdate={setDidProSettingsUpdate}>
                        {isActive && newProSettings && !!newProSettings?.length && (
                            <NewEditMicrosite micrositeId={micrositeId} newProSettings={newProSettings} />
                        )}
                        {isActive && loadingProSettings && <div style={{
                            marginTop: '3rem',
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <Spin className='' tip="Loading" size='large' />
                        </div>}
                    </MicroSiteAccordian>
                })
            }

        </div>
    )
}


export default withMicroSites(React.memo(MicroSites))
